import React, { useState, useEffect } from 'react';
import { sanitizeImageUrl } from '../utils/api';

function useCountdown(targetIso) {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!targetIso) return 0;
    return Math.max(0, Math.floor((new Date(targetIso).getTime() - Date.now()) / 1000));
  });

  useEffect(() => {
    if (!targetIso) { setSecondsLeft(0); return; }
    const calc = () => Math.max(0, Math.floor((new Date(targetIso).getTime() - Date.now()) / 1000));
    setSecondsLeft(calc());
    const id = setInterval(() => setSecondsLeft(calc()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);

  return secondsLeft;
}

export default function WarHeader({ t, war }) {
  const countdownTarget = war
    ? (war.state === 'preparation' ? war.startTime : war.state === 'inWar' ? war.endTime : null)
    : null;
  const secondsLeft = useCountdown(countdownTarget);

  if (!war) {
    return (
      <div id="warHeader" className="mb-6 p-6 bg-gray-800 rounded-xl border border-gray-700 shadow-lg">
        <div className="text-center py-8 text-gray-400">
          <div className="text-2xl mb-2">⚔️</div>
          <div>{t('no_war')}</div>
        </div>
      </div>
    );
  }

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);

  const isEnded = war.state === 'warEnded';
  const isVictory = isEnded && (war.clan_stars > war.enemy_stars || (war.clan_stars === war.enemy_stars && (war.clan_destruction ?? 0) > (war.enemy_destruction ?? 0)));
  const isDraw = isEnded && war.clan_stars === war.enemy_stars && Math.abs((war.clan_destruction ?? 0) - (war.enemy_destruction ?? 0)) < 0.01;

  let stateLabel = '', stateBgColor = '', stateIcon = '';
  if (war.state === 'preparation') {
    stateLabel = t('state_preparation'); stateBgColor = 'bg-yellow-600'; stateIcon = '⏳';
  } else if (war.state === 'inWar') {
    stateLabel = t('state_in_war'); stateBgColor = 'bg-blue-600'; stateIcon = '⚔️';
  } else if (isVictory) {
    stateLabel = t('state_victory'); stateBgColor = 'bg-green-600'; stateIcon = '🏆';
  } else if (isDraw) {
    stateLabel = t('state_draw'); stateBgColor = 'bg-yellow-600'; stateIcon = '🤝';
  } else {
    stateLabel = t('state_defeat'); stateBgColor = 'bg-red-800'; stateIcon = '💀';
  }

  let endsAtText = '';
  const formatDateParts = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return { date: `${day}.${month}.${year}`, time: `${hours}:${minutes}` };
  };

  if (war.state === 'preparation' && war.startTime) {
    const { date, time } = formatDateParts(war.startTime);
    endsAtText = t('starts_at', { date, time });
  } else if (war.state === 'inWar' && war.endTime) {
    const { date, time } = formatDateParts(war.endTime);
    endsAtText = t('ends_at', { date, time });
  } else if (war.endTime) {
    const { date, time } = formatDateParts(war.endTime);
    endsAtText = t('ended_at', { date, time });
  }

  const attackPct = war.total_attacks > 0 ? (war.attacks_used / war.total_attacks * 100) : 0;
  const enemyAttackPct = war.total_attacks > 0 ? (war.enemy_attacks_used / war.total_attacks * 100) : 0;

  const winProb = war.win_probability ?? 0;
  const probColor = winProb >= 60 ? '#34d399' : winProb >= 40 ? '#fbbf24' : '#f87171';

  return (
    <div id="warHeader" className="mb-6 p-6 bg-gray-800 rounded-xl border border-gray-700 shadow-lg">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          {/* Our Clan */}
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className="relative mb-2">
              <img src={sanitizeImageUrl(war.clan_badge)} className="w-14 h-14 md:w-20 md:h-20 rounded-lg shadow-lg" alt={war.clan_name} />
              <div className="absolute -bottom-1 -right-1 bg-blue-600 rounded-full px-2 py-1 text-xs font-bold">
                {war.clan_stars} ⭐
              </div>
            </div>
            <div className="text-sm font-bold text-center line-clamp-2 break-words w-full">{war.clan_name}</div>
            <div className="text-xs text-gray-400 mt-1">{(war.clan_destruction ?? 0).toFixed(1)}% 💥</div>
          </div>

          {/* State */}
          <div className="flex flex-col items-center min-w-[80px]">
            <div className={`${stateBgColor}${war.state === 'inWar' ? ' wh-inwar-blink' : ''} text-white rounded-xl px-3 py-2 text-center font-bold text-sm mb-2`}>
              <div>{stateIcon}</div>
              <div className="text-xs">{stateLabel}</div>
            </div>
            <div className="text-2xl font-bold">VS</div>
          </div>

          {/* Enemy */}
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className="relative mb-2">
              <img src={sanitizeImageUrl(war.enemy_badge)} className="w-14 h-14 md:w-20 md:h-20 rounded-lg shadow-lg" alt={war.enemy_name} />
              <div className="absolute -bottom-1 -right-1 bg-red-600 rounded-full px-2 py-1 text-xs font-bold">
                {war.enemy_stars} ⭐
              </div>
            </div>
            <div className="text-sm font-bold text-center line-clamp-2 break-words w-full">{war.enemy_name}</div>
            <div className="text-xs text-gray-400 mt-1">{(war.enemy_destruction ?? 0).toFixed(1)}% 💥</div>
          </div>
        </div>

        {/* Attack bars */}
        <div className="space-y-4">
          <div className="text-sm font-semibold text-center">⚔️ {t('remaining_attacks')}</div>
          <div className="grid grid-cols-2 gap-4 md:gap-24">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="text-xs font-bold">{war.attacks_used} / {war.total_attacks}</div>
                <div className="text-xs text-blue-400 font-semibold">⭐ {war.clan_avg_stars}</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-300" style={{ width: `${attackPct}%` }} />
              </div>
              <div className="text-xs text-gray-400 text-center">{Math.round(attackPct)}%</div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <div className="text-xs text-red-400 font-semibold">⭐ {war.enemy_avg_stars}</div>
                <div className="text-xs font-bold">{war.enemy_attacks_used} / {war.total_attacks}</div>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-red-600 h-full transition-all duration-300" style={{ width: `${enemyAttackPct}%` }} />
              </div>
              <div className="text-xs text-gray-400 text-center">{Math.round(enemyAttackPct)}%</div>
            </div>
          </div>

          {/* Win probability */}
          <div className={`wh-stat-box ${(war.win_probability ?? 0) >= 60 ? 'wh-stat-bg-green' : (war.win_probability ?? 0) >= 40 ? 'wh-stat-bg-yellow' : 'wh-stat-bg-red'}`}>
            <div className="wh-stat-box-label">🏆 {t('win_probability')}</div>
            <div className={`wh-stat-box-value ${(war.win_probability ?? 0) >= 60 ? 'wh-stat-green' : (war.win_probability ?? 0) >= 40 ? 'wh-stat-yellow' : 'wh-stat-red'}`}>
              {(war.win_probability ?? 0).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Time / Result */}
        <div className="text-center">
          {isEnded ? (
            <div className={`wh-result-box ${isVictory ? 'wh-result-victory' : isDraw ? 'wh-result-draw' : 'wh-result-defeat'}`}>
              <div className="wh-result-label">{stateIcon} {stateLabel}</div>
              {endsAtText && <div className="wh-result-sub">{endsAtText}</div>}
            </div>
          ) : (
            <div className="wh-countdown-card">
              <div className="wh-countdown-card-label">⏱️ {t('remaining_time')}</div>
              <div className="wh-countdown-card-time">{hours}h {minutes}m {secondsLeft % 60}s</div>
              {endsAtText && <div className="wh-countdown-card-sub">{endsAtText}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
